import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  LinearProgress,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useSupplierIntegration, useUpdateSupplierIntegration } from '@/hooks/useInventory';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import {
  AUTH_TYPES,
  SUPPLIER_OPERATION_KEYS,
  buildSupplierIntegrationRequest,
  formValuesFromIntegration,
  genericRestAdapterConfigSchema,
  isEncryptionKeyUnavailable,
  isSupplierAuthType,
  stripSecretKeys,
  type SupplierIntegrationField,
  type SupplierIntegrationFieldErrors,
  type SupplierIntegrationFormValues,
  type SupplierIntegrationView,
  type SupplierOperationKey,
} from './supplierIntegration';

interface SupplierIntegrationDialogProps {
  open: boolean;
  supplier: { id: string; name: string } | null;
  onClose: () => void;
}

function showValue(value: string, empty: string): string {
  return value.trim() !== '' ? value : empty;
}

function IntegrationStatusHeader({
  view,
  canEdit,
}: {
  view: SupplierIntegrationView;
  canEdit: boolean;
}) {
  const { t } = useTranslation('inventory');
  return (
    <Stack spacing={1}>
      <Chip
        size="small"
        sx={{ alignSelf: 'flex-start' }}
        color={view.configured ? 'success' : 'default'}
        label={view.configured ? t('suppliers.statusConfigured') : t('suppliers.statusUnconfigured')}
      />
      {!canEdit && <Alert severity="info">{t('suppliers.integration.readOnlyHint')}</Alert>}
      {!view.encryptionAvailable && (
        <Alert severity="warning">{t('suppliers.integration.encryptionUnavailable')}</Alert>
      )}
    </Stack>
  );
}

function ReadOnlyLine({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function operationRows(config: unknown): { key: SupplierOperationKey; method: string; path: string }[] {
  const parsed = genericRestAdapterConfigSchema.safeParse(stripSecretKeys(config));
  if (!parsed.success) return [];
  const rows: { key: SupplierOperationKey; method: string; path: string }[] = [];
  for (const key of SUPPLIER_OPERATION_KEYS) {
    const operation = parsed.data[key];
    if (!operation) continue;
    rows.push({ key, method: operation.method, path: operation.path });
  }
  return rows;
}

function SupplierIntegrationReadOnly({
  view,
  onClose,
}: {
  view: SupplierIntegrationView;
  onClose: () => void;
}) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const empty = t('suppliers.integration.emptyValue');
  const parsed = view.configured
    ? genericRestAdapterConfigSchema.safeParse(stripSecretKeys(view.config))
    : null;
  const config = parsed && parsed.success ? parsed.data : null;
  const rows = view.configured && config ? operationRows(view.config) : [];

  return (
    <>
      <DialogContent>
        <Stack spacing={2}>
          <IntegrationStatusHeader view={view} canEdit={false} />
          {!view.configured && (
            <Typography variant="body2">{t('suppliers.integration.notConfiguredBody')}</Typography>
          )}
          {parsed && !parsed.success && (
            <Alert severity="warning">{t('suppliers.integration.configUnreadable')}</Alert>
          )}
          {config && (
            <>
              <ReadOnlyLine
                label={t('suppliers.integration.provider')}
                value={showValue(view.configured ? view.provider : '', empty)}
              />
              <ReadOnlyLine
                label={t('suppliers.integration.baseUrl')}
                value={showValue(config.baseUrl, empty)}
              />
              <ReadOnlyLine
                label={t('suppliers.integration.authType')}
                value={t(`suppliers.integration.authTypes.${config.auth.type}`)}
              />
              {(config.auth.type === 'header' || config.auth.type === 'query') && (
                <ReadOnlyLine
                  label={t('suppliers.integration.authName')}
                  value={showValue(config.auth.name ?? '', empty)}
                />
              )}
              <ReadOnlyLine
                label={t('suppliers.integration.currency')}
                value={showValue(config.currency, empty)}
              />
              <Box>
                <Typography variant="caption" color="text.secondary" component="div">
                  {t('suppliers.integration.operations')}
                </Typography>
                {rows.length === 0 ? (
                  <Typography variant="body2">{t('suppliers.integration.noOperations')}</Typography>
                ) : (
                  rows.map((row) => (
                    <Typography key={row.key} variant="body2">
                      {t(`suppliers.integration.operationLabels.${row.key}`)}: {row.method} {row.path}
                    </Typography>
                  ))
                )}
              </Box>
            </>
          )}
          {view.configured && (
            <Typography variant="body2">
              {view.hasCredentials
                ? t('suppliers.integration.hasCredentials')
                : t('suppliers.integration.noCredentials')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{tc('close')}</Button>
      </DialogActions>
    </>
  );
}

function SupplierIntegrationEditor({
  supplierId,
  view,
  onClose,
}: {
  supplierId: string;
  view: SupplierIntegrationView;
  onClose: () => void;
}) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const update = useUpdateSupplierIntegration();
  const [form, setForm] = useState<SupplierIntegrationFormValues>(() => formValuesFromIntegration(view));
  const [errors, setErrors] = useState<SupplierIntegrationFieldErrors>({});
  const [encryptionError, setEncryptionError] = useState(false);

  const clearError = (field: SupplierIntegrationField) => {
    setErrors((current) => {
      if (current[field] === undefined) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const setField = <K extends keyof SupplierIntegrationFormValues>(
    key: K,
    value: SupplierIntegrationFormValues[K],
    errorField?: SupplierIntegrationField,
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (errorField) clearError(errorField);
  };

  const handleSave = () => {
    const result = buildSupplierIntegrationRequest(form);
    if (!result.ok) {
      setErrors(result.errors);
      setEncryptionError(false);
      return;
    }
    setErrors({});
    setEncryptionError(false);
    update.mutate(
      { supplierId, body: result.body },
      {
        onSuccess: () => {
          notify.success(t('suppliers.integration.saved'), t('suppliers.integration.savedDesc'));
          onClose();
        },
        onError: (error) => {
          if (isEncryptionKeyUnavailable(error)) {
            setEncryptionError(true);
            return;
          }
          setEncryptionError(false);
          notify.error(tc('error'), describeApiError(error, tc, { fallback: t('suppliers.integration.saveFailed') }));
        },
      },
    );
  };

  const showAuthName = form.authType === 'header' || form.authType === 'query';

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        handleSave();
      }}
    >
      <DialogContent>
        <Stack spacing={2}>
          <IntegrationStatusHeader view={view} canEdit />
          {encryptionError && (
            <Alert severity="error">{t('suppliers.integration.encryptionKeyMissing')}</Alert>
          )}
          <TextField
            label={t('suppliers.integration.provider')}
            value={form.provider}
            onChange={(event) => setField('provider', event.target.value, 'provider')}
            error={errors.provider !== undefined}
            helperText={errors.provider ? t(errors.provider) : t('suppliers.integration.providerHint')}
            autoComplete="off"
            fullWidth
          />
          <TextField
            label={t('suppliers.integration.baseUrl')}
            value={form.baseUrl}
            onChange={(event) => setField('baseUrl', event.target.value, 'baseUrl')}
            error={errors.baseUrl !== undefined}
            helperText={errors.baseUrl ? t(errors.baseUrl) : ''}
            autoComplete="off"
            fullWidth
          />
          <TextField
            select
            label={t('suppliers.integration.authType')}
            value={form.authType}
            onChange={(event) => {
              const next = event.target.value;
              if (isSupplierAuthType(next)) setField('authType', next, 'authName');
            }}
            fullWidth
          >
            {AUTH_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`suppliers.integration.authTypes.${type}`)}
              </MenuItem>
            ))}
          </TextField>
          {showAuthName && (
            <TextField
              label={t('suppliers.integration.authName')}
              value={form.authName}
              onChange={(event) => setField('authName', event.target.value, 'authName')}
              error={errors.authName !== undefined}
              helperText={errors.authName ? t(errors.authName) : ''}
              autoComplete="off"
              fullWidth
            />
          )}
          <TextField
            label={t('suppliers.integration.secret')}
            type="password"
            value={form.secret}
            onChange={(event) => setField('secret', event.target.value, 'secret')}
            error={errors.secret !== undefined}
            helperText={
              errors.secret
                ? t(errors.secret)
                : view.configured && view.hasCredentials
                  ? t('suppliers.integration.secretKept')
                  : t('suppliers.integration.secretHint')
            }
            autoComplete="new-password"
            fullWidth
          />
          <TextField
            label={t('suppliers.integration.currency')}
            value={form.currency}
            onChange={(event) => setField('currency', event.target.value, 'currency')}
            error={errors.currency !== undefined}
            helperText={errors.currency ? t(errors.currency) : ''}
            autoComplete="off"
            fullWidth
            slotProps={{ htmlInput: { maxLength: 3 } }}
          />
          <TextField
            label={t('suppliers.integration.operations')}
            value={form.operationsJson}
            onChange={(event) => setField('operationsJson', event.target.value, 'operations')}
            error={errors.operations !== undefined}
            helperText={
              errors.operations ? t(errors.operations) : t('suppliers.integration.operationsHint')
            }
            fullWidth
            multiline
            minRows={6}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
            slotProps={{ htmlInput: { spellCheck: false } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button type="button" onClick={onClose} disabled={update.isPending}>
          {tc('cancel')}
        </Button>
        <Button type="submit" variant="contained" disabled={update.isPending}>
          {update.isPending ? tc('saving') : tc('save')}
        </Button>
      </DialogActions>
    </Box>
  );
}

function SupplierIntegrationBody({
  supplierId,
  onClose,
}: {
  supplierId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const canEdit = useAuthStore((s) => s.hasPermission('settings.edit_workshop'));
  const query = useSupplierIntegration(supplierId);

  if (query.isPending) {
    return (
      <>
        <DialogContent>
          <LinearProgress />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>{tc('close')}</Button>
        </DialogActions>
      </>
    );
  }

  if (query.isError || !query.data) {
    return (
      <>
        <DialogContent>
          <Alert severity="error">{t('suppliers.integration.loadFailed')}</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>{tc('close')}</Button>
        </DialogActions>
      </>
    );
  }

  if (!canEdit) {
    return <SupplierIntegrationReadOnly view={query.data} onClose={onClose} />;
  }

  return (
    <SupplierIntegrationEditor
      key={supplierId}
      supplierId={supplierId}
      view={query.data}
      onClose={onClose}
    />
  );
}

export function SupplierIntegrationDialog({ open, supplier, onClose }: SupplierIntegrationDialogProps) {
  const { t } = useTranslation('inventory');
  const title = t('suppliers.integration.title', { name: supplier?.name ?? '' });

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth mobileTitle={title}>
      <DialogTitle>{title}</DialogTitle>
      {open && supplier ? (
        <SupplierIntegrationBody key={supplier.id} supplierId={supplier.id} onClose={onClose} />
      ) : null}
    </ResponsiveDialog>
  );
}
